import type { ReactNode } from "react";
import {
  Avatar,
  Chip as TwentyChip,
  ChipSize,
  ChipVariant,
  Status,
  Tag,
} from "twenty-ui/data-display";
import {
  Button as TwentyButton,
  IconButton,
  SearchInput,
} from "twenty-ui/input";
import type { ButtonProps as TwentyButtonProps } from "twenty-ui/input";
import { Banner, Loader } from "twenty-ui/feedback";
import { AppTooltip, Card, CardContent, CardHeader } from "twenty-ui/surfaces";
import { ThemeProvider } from "twenty-ui/theme-constants";
import type { IconComponent } from "twenty-ui/icon";
import {
  IconAdjustments,
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconBriefcase,
  IconBuilding,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconColumns,
  IconCommand,
  IconCode,
  IconDotsVertical,
  IconExternalLink,
  IconFilter,
  IconLayoutKanban,
  IconLayoutList,
  IconLayoutNavbar,
  IconMailPlus,
  IconRefresh,
  IconSearch,
  IconUsers,
  IconX,
} from "@tabler/icons-react";

export {
  AppTooltip,
  Avatar,
  Banner,
  Card,
  CardContent,
  CardHeader,
  ChipSize,
  ChipVariant,
  IconButton,
  Loader,
  SearchInput,
  Status,
  Tag,
  IconAdjustments,
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconBriefcase,
  IconBuilding,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconColumns,
  IconCommand,
  IconCode,
  IconDotsVertical,
  IconExternalLink,
  IconFilter,
  IconLayoutKanban,
  IconLayoutList,
  IconLayoutNavbar,
  IconMailPlus,
  IconRefresh,
  IconSearch,
  IconUsers,
  IconX,
};

// Preserve the ergonomic enum access used by the page layer while keeping the
// actual component implementation supplied by Twenty.
export const Chip = Object.assign(TwentyChip, {
  Variant: ChipVariant,
  Size: ChipSize,
});

export type { IconComponent };

type CrmButtonProps = TwentyButtonProps & { children?: ReactNode };

/** Keeps the page layer independent from Twenty's title-only Button API. */
export function Button({
  children,
  Icon,
  variant = "tertiary",
  size = "small",
  ...props
}: CrmButtonProps) {
  if (children === undefined) {
    return (
      <TwentyButton {...props} Icon={Icon} variant={variant} size={size} />
    );
  }
  return (
    <button
      {...props}
      type={props.type ?? "button"}
      className={`crm-button crm-button--${variant} crm-button--${size} ${props.className ?? ""}`.trim()}
      aria-label={
        props.ariaLabel ??
        (typeof children === "string" ? children : props.title)
      }
      disabled={props.disabled}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </button>
  );
}

export function CrmUiProvider({
  children,
  dark = false,
}: {
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <ThemeProvider colorScheme={dark ? "dark" : "light"} applyToRoot>
      {children}
    </ThemeProvider>
  );
}

export function CrmSelect({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="crm-select">
      <span>{label}</span>
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <IconChevronDown size={14} />
    </label>
  );
}

export function CrmIconButton({
  label,
  icon: Icon,
  onClick,
  disabled = false,
}: {
  label: string;
  icon: IconComponent;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <IconButton
      ariaLabel={label}
      Icon={Icon}
      onClick={onClick ? () => onClick() : undefined}
      disabled={disabled}
      dataTestId={`icon-${label}`}
    />
  );
}
